//@flow
import React, {useCallback, useState} from 'react';
import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import {useProductContext} from '../../../../context';
import CustomTextField from '../../../../../../elements/CustomTextField/CustomTextField';
import {useForm} from 'react-hook-form';
import {AddCircleTwoTone, DeleteOutline} from '@material-ui/icons';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../../assets/styles/colors';
import {debounce} from '../../../../../../utils/functions';
import {useEditProduct} from '../../../../utils/editProduct';
import {useIsFeatureInTier} from '../../../../../../utils/component';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 700,
    width: '90vw',
    gap: '1rem',
    overflow: 'hidden',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
    color: colors.grey800,
  },
  content: {
    width: '100%',
    height: '100%',
    maxHeight: 'calc(100vh - 300px)',
    overflow: 'auto',
  },
  submitButton: {
    maxWidth: '240px',
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  addLineButton: {
    marginTop: '1rem',
  },
  errorMessage: {
    color: colors.danger,
    whiteSpace: 'nowrap',
    width: '100%',
  },
}));

type BulkPriceLine = {
  minQuantity: number,
  unitPrice: string,
  taxedUnitPrice: string,
};

type Props = {
  onSubmit: (prices: BulkPriceLine[]) => void,
  bulkPrices: BulkPriceLine[],
  taxFactor: number,
};

export default function WholeSaleModal(props: Props) {
  const classes = useStyles();

  const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const [{offerPrice, price, productId}] = useProductContext(product => ({
    productId: product.id,
    offerPrice: product.taxedOfferPrice,
    price: product.taxedPrice,
  }));

  const {editProduct} = useEditProduct(productId);
  const product = useProductContext(p => p);

  const [bulkPriceLines, setBulkPriceLines] = useState<BulkPriceLine[]>(
    props.bulkPrices
  );

  const productTaxFactor = props.taxFactor;

  const validateBulkPriceLineUnitPrice = (value: string, index: number) => {
    console.log({
      offerPrice,
      price,
      product,
      bulkPriceUnitPrice: value,
    });
    const bulkPriceUnitPrice = parseFloat(value);
    if (offerPrice) {
      if (bulkPriceUnitPrice >= parseFloat(offerPrice))
        return 'El precio unitario debe ser menor que el precio de oferta del producto';
    }

    if (price) {
      if (bulkPriceUnitPrice >= parseFloat(price))
        return 'El precio unitario debe ser menor que el precio de venta del producto';
    } else return true;
  };

  const calcPriceWithTax = useCallback(
    (value: number) => {
      return parseFloat(value * productTaxFactor).toFixed(5);
    },
    [productTaxFactor]
  );

  const calcPriceWithoutTax = useCallback(
    (value: number) => {
      return parseFloat(value / productTaxFactor).toFixed(5);
    },
    [productTaxFactor]
  );

  const {
    register: regBulk,
    errors: regErrors,
    setError,
    clearErrors,
    handleSubmit,
  } = useForm();

  function validateBulk() {
    const tempBulk = [...bulkPriceLines];
    let hasError = false;
    clearErrors();
    tempBulk.forEach((e, i) => {
      if (Number(e.unitPrice) === 0) {
        setError(`unitPrice-${i}`, {message: 'Precio debe ser mayor a 0'});
        hasError = true;
      }
      if (i === 0 && (e.minQuantity < 2 || !e.minQuantity)) {
        setError(`minimumQuantity-${i}`, {
          message: 'La cantidad no puede ser menor a 2 unidades',
        });
        hasError = true;
      } else if (i > 0 && i < tempBulk.length) {
        if (e.minQuantity <= tempBulk[i - 1].minQuantity) {
          setError(`minimumQuantity-${i}`, {
            message: 'La cantidad debe ser mayor a la fila anterior',
          });
          hasError = true;
        }
      }
    });
    return !hasError;
  }

  const handleAddLine = () => {
    setBulkPriceLines([
      ...bulkPriceLines,
      {minQuantity: 0, unitPrice: '0', taxedUnitPrice: '0'},
    ]);
  };

  const evaluateMinQuantity = (e, index) => {
    const tempBulkPriceLines = [...bulkPriceLines];
    tempBulkPriceLines[index].minQuantity = parseInt(e.target.value);
    setBulkPriceLines(tempBulkPriceLines);
    // setInputChange(true);
  };

  const evaluateMaxQuantity = (arr, index) => {
    if (
      arr[index + 1].minQuantity === 0 ||
      !parseInt(arr[index + 1].minQuantity)
    ) {
      return '';
    } else {
      return arr[index + 1].minQuantity - 1;
    }
  };

  const evaluateTaxedPrice = (value, index, taxed = false) => {
    const tempBulkPriceLines = [...bulkPriceLines];
    if (taxed) {
      tempBulkPriceLines[index].taxedUnitPrice = calcPriceWithTax(value);
    } else {
      tempBulkPriceLines[index].taxedUnitPrice = value;
      tempBulkPriceLines[index].unitPrice = calcPriceWithoutTax(value);
    }

    setBulkPriceLines(tempBulkPriceLines);
  };

  const evaluatePrice = (e, index) => {
    const tempBulkPriceLines = [...bulkPriceLines];
    tempBulkPriceLines[index].unitPrice = e.target.value;
    setBulkPriceLines(tempBulkPriceLines);
  };

  const handleRemoveLine = index => {
    const tempBulkPriceLines = [...bulkPriceLines];
    tempBulkPriceLines.splice(index, 1);
    setBulkPriceLines(tempBulkPriceLines);
  };

  const editBulkPrice = debounce(() => {
    editProduct({
      bulkPriceSetting: {
        bulkPriceLines: bulkPriceLines.map(el => {
          const {taxedUnitPrice, ...data} = el;
          data.unitPrice = taxedUnitPrice;
          return data;
        }),
      },
    });

    props.onSubmit(bulkPriceLines);
  }, 750);

  const onSubmit = data => {
    const validFields = validateBulk();

    if (validFields) {
      editBulkPrice();
    }
  };

  return (
    <aside className={classes.container}>
      <Typography className={classes.title} align="center">
        Precio Mayorista
      </Typography>
      <div className={classes.content}>
        <TableContainer>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  style={{padding: 4, fontSize: '0.75rem'}}
                  align="left"
                >
                  Desde
                </TableCell>
                <TableCell style={{padding: 4}} align="left"></TableCell>
                <TableCell
                  style={{padding: 4, fontSize: '0.75rem'}}
                  align="left"
                >
                  Hasta
                </TableCell>
                <TableCell
                  style={{padding: 4, fontSize: '0.75rem'}}
                  align="left"
                >
                  Valor por unidad
                </TableCell>
                <TableCell
                  style={{padding: 4, fontSize: '0.75rem'}}
                  align="left"
                >
                  Precio por unidad
                </TableCell>
                <TableCell
                  style={{padding: 4, fontSize: '0.75rem'}}
                  align="left"
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkPriceLines.map((line, index) => (
                <React.Fragment key={`line-${index}`}>
                  <TableRow key={index}>
                    <TableCell
                      component="th"
                      scope="row"
                      padding="none"
                      align="left"
                      style={{padding: 4}}
                    >
                      <CustomTextField
                        id="minimum-quantity"
                        size="small"
                        name={`minimumQuantity-${index}`}
                        error={!!regErrors[`minimumQuantity-${index}`]}
                        label="Cant."
                        value={line.minQuantity || ''}
                        type="text"
                        inputRef={regBulk({
                          required:
                            'La cantidad no puede ser menor a 2 unidades',
                        })}
                        helperText={
                          regErrors[`minimumQuantity-${index}`] &&
                          regErrors[`minimumQuantity-${index}`].message
                        }
                        className={classes.quantityInputField}
                        onChange={e => evaluateMinQuantity(e, index)}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      align="center"
                      padding="none"
                      style={{
                        padding: 4,
                      }}
                    >
                      -
                    </TableCell>
                    {index === bulkPriceLines.length - 1 ? (
                      <TableCell
                        component="th"
                        padding="none"
                        align="left"
                        style={{
                          padding: 4,
                        }}
                      >
                        <Typography variant="body2" className={classes.more}>
                          A más
                        </Typography>
                      </TableCell>
                    ) : (
                      <TableCell
                        component="th"
                        align="left"
                        padding="none"
                        style={{padding: 4}}
                      >
                        <CustomTextField
                          id="maximum-quantity"
                          size="small"
                          name="maximumQuantity"
                          autoComplete="maximumQuantity"
                          margin="none"
                          value={evaluateMaxQuantity(bulkPriceLines, index)}
                          type="text"
                          style={{marginTop: 0}}
                          disabled={true}
                          className={classes.quantityInputField}
                        />
                      </TableCell>
                    )}

                    <TableCell
                      component="th"
                      align="left"
                      padding="none"
                      style={{padding: 4}}
                    >
                      <CustomTextField
                        id="unit-price"
                        size="small"
                        name={`unitPrice-${index}`}
                        error={!!regErrors[`unitPrice-${index}`]}
                        helperText={
                          regErrors[`unitPrice-${index}`] &&
                          regErrors[`unitPrice-${index}`].message
                        }
                        label="Valor por unidad"
                        type="text"
                        value={line.unitPrice || ''}
                        inputRef={regBulk({
                          pattern: {
                            value: /^\d+(\.\d{1,5})?$/,
                            message: 'Ingrese un monto válido',
                          },
                        })}
                        className={classes.quantityInputField}
                        onChange={e => {
                          const value = e.target.value;
                          if (!isNaN(Number(value)))
                            evaluateTaxedPrice(value, index, true);
                          evaluatePrice(e, index);
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      align="left"
                      padding="none"
                      style={{padding: 4}}
                    >
                      <CustomTextField
                        id="taxed-unit-price"
                        size="small"
                        name={`taxed-unitPrice-${index}`}
                        /* error={!!regErrors[`taxed-unitPrice-${index}`]}
                        helperText={
                          regErrors[`taxed-unitPrice-${index}`] &&
                          regErrors[`taxed-unitPrice-${index}`].message
                        } */
                        label="Precio por unidad"
                        disabled={!hasTaxesFeature}
                        type="text"
                        value={line.taxedUnitPrice || ''}
                        inputRef={regBulk({
                          pattern: {
                            value: /^\d+(\.\d{1,5})?$/,
                            message: 'Ingrese un monto válido',
                          },
                          validate: value =>
                            validateBulkPriceLineUnitPrice(value, index),
                        })}
                        className={classes.quantityInputField}
                        onChange={e => {
                          const value = e.target.value;
                          if (!isNaN(Number(value)))
                            evaluateTaxedPrice(value, index);
                        }}
                      />
                    </TableCell>
                    <TableCell component="th" align="center" padding="none">
                      <div>
                        <Button
                          className={classes.deleteIcon}
                          onClick={() => handleRemoveLine(index)}
                          // disabled={bulkPriceLines.length === 1}
                        >
                          <DeleteOutline color="error" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>

                  {!!regErrors[`taxed-unitPrice-${index}`] && (
                    <TableRow>
                      <TableCell colSpan={6} className={classes.errorMessage}>
                        {regErrors[`taxed-unitPrice-${index}`].message}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomButton
          onClick={handleAddLine}
          className={classes.addLineButton}
          startIcon={<AddCircleTwoTone />}
        >
          Añadir otra fila precio
        </CustomButton>
      </div>

      <CustomButton
        className={classes.submitButton}
        color="primary"
        variant="contained"
        fullWidth
        onClick={handleSubmit(onSubmit)}
      >
        Guardar
      </CustomButton>
    </aside>
  );
}
